import {CellContent, EditorType} from "./CellContent";
import {ContentHeader} from "./ContentHeader";
import {Funnel} from "./Funnel";
import {RoleHeader} from "./RoleHeader";
import {SerializableJourneyMap} from "./SerializableJourneyMap";
import {BackgroundKey2Src} from "../assets/BackgroundImage";
import {ContentKey, ContentKeyPair, FunnelKey, RoleKey, RoleKeyPair} from "./Keys";
import {RoleCell, VisibleRoleCell} from "./RoleCell";
import {FunnelGroupCell, FunnelGroupLine, VisibleFunnelGroupCell} from "./FunnelGroupLine";
import {CjmapCells} from "./CjmapCells";
import {ContentCell, ContentCells} from "./ContentCell";
import {RoleLine, RoleLines} from "./RoleLines";
import {CjmapSelection} from "./CjmapSelection";
import {MainFunnelCell, MainFunnelLine} from "./MainFunnelCell";
import {CjmapPersona} from "./CjmapPersona";
import {ColorScheme, DEFAULT_COLOR_SCHEME_KEY, findColorSchemeOrDefault} from "../assets/ColorSchema";
import {debug_log, is_dev} from "../utils/debug";
import {CjmapCollaboratorFocus} from "./CjmapCollaboratorFocus";


export type HiddenBy = 'both' | 'column' | 'row' | 'none';

function makeHiddenBy(hidden_by_funnel: boolean, hidden_by_line: boolean): HiddenBy {

    if (hidden_by_funnel && hidden_by_line) {
        return 'both';
    } else if (hidden_by_funnel) {
        return 'column';
    } else if (hidden_by_line) {
        return 'row';
    } else {
        return 'none';
    }
}

export type FunnelType = 'single' | 'double';

export type MoveDirection = 'left' | 'right' | 'up' | 'down';

type CJmapManagerConstruction = {
    title?: string;
    background_key?: string;
    color_scheme_key?: string;
    funnel_type?: FunnelType;
    merge?: boolean;
    cells?: CjmapCells;
    selection?: CjmapSelection;
    collaborator_focus?: CjmapCollaboratorFocus;
    persona?: CjmapPersona;
}

const DO_NOTHING = () => {
};

export class CJMapManager {
    private readonly _version: number;
    private readonly title: string;
    private readonly background_key: string;
    private readonly color_scheme_key: string;
    private readonly funnel_type: FunnelType;
    private readonly merge: boolean;
    private readonly cells: CjmapCells;
    private readonly selection: CjmapSelection;
    private readonly collaborator_focus: CjmapCollaboratorFocus;

    private readonly _funnel_group_line: FunnelGroupLine;
    private readonly _main_funnel_line: MainFunnelLine;
    private readonly _content_cells: ContentCells;
    private readonly _role_lines: RoleLines;

    private readonly persona: CjmapPersona;

    public onUpdateSelection: (before: CjmapSelection, after: CjmapSelection) => void = DO_NOTHING;

    private clone(version: number, replacement: CJmapManagerConstruction) {

        if (replacement.selection !== undefined) {
            this.onUpdateSelection(this.selection, replacement.selection);
        }

        return new CJMapManager(version, {
            title: replacement.title !== undefined ? replacement.title : this.title,
            background_key: replacement.background_key !== undefined ? replacement.background_key : this.background_key,
            color_scheme_key: replacement.color_scheme_key !== undefined ? replacement.color_scheme_key : this.color_scheme_key,
            funnel_type: replacement.funnel_type !== undefined ? replacement.funnel_type : this.funnel_type,
            merge: replacement.merge !== undefined ? replacement.merge : this.merge,
            cells: replacement.cells !== undefined ? replacement.cells : this.cells,
            selection: replacement.selection !== undefined ? replacement.selection : this.selection,
            collaborator_focus: replacement.collaborator_focus !== undefined ? replacement.collaborator_focus : this.collaborator_focus,
            persona: replacement.persona !== undefined ? replacement.persona : this.persona,
        });
    }

    static createNew() {
        return new CJMapManager(0, {});
    }

    private constructor(version: number, construction: CJmapManagerConstruction) {

        this.title = construction.title !== undefined ? construction.title : '*empty*';
        this.background_key = construction.background_key !== undefined ? construction.background_key : '';
        this.color_scheme_key = construction.color_scheme_key !== undefined ? construction.color_scheme_key : DEFAULT_COLOR_SCHEME_KEY;
        this.funnel_type = construction.funnel_type !== undefined ? construction.funnel_type : 'double';
        this.merge = construction.merge !== undefined ? construction.merge : true;
        this.cells = construction.cells !== undefined ? construction.cells : CjmapCells.createNew();
        this.selection = construction.selection !== undefined ? construction.selection : CjmapSelection.createNew();
        this.collaborator_focus = construction.collaborator_focus !== undefined ? construction.collaborator_focus : CjmapCollaboratorFocus.createNew();
        this.persona = construction.persona !== undefined ? construction.persona : CjmapPersona.createDefaultPersona();
        this._version = version;

        this._funnel_group_line = this.makeFunnelGroupLine();
        this._main_funnel_line = this.makeMainFunnelLine();
        this._content_cells = this.makeContentCells();
        this._role_lines = this.makeRoleLines();
    }

    private makeFunnelGroupLine(): FunnelGroupLine {
        const cells = this.FunnelHeaders.map(f => new FunnelGroupCell(f.Key, f.Group));

        const funnel_group_cells = FunnelGroupLine.makeVisibleCells(cells, this.merge,
            v => {
                const column_start = v.offset + 1;
                const span_num = v.keys.length;
                const grid_column = `${column_start}` + (1 < span_num ? `/span ${span_num}` : '');

                const is_selected = v.keys.some(key => this.selection.selectedFunnelGroup(key));
                const has_next_cell = v.next !== null;
                const is_joined = 1 < v.keys.length;

                return new VisibleFunnelGroupCell(
                    v.cell,
                    {
                        IsSelected: is_selected,
                        BackgroundColor: this.ColorScheme.GroupColor,
                        GridColumn: this.merge ? grid_column : 'auto',
                        PrevFunnelKeyOrNull: v.prev,
                        NextFunnelKeyOrNull: v.next,
                        IsJoinable: has_next_cell,
                        IsSeparatable: is_joined,
                        JoinedKeys: v.keys,
                        Collaborators: this.collaborator_focus.getFocusUserLabelsInFunnelGroupCell(v.cell.Key),
                    }
                );
            }
        );

        return new FunnelGroupLine(funnel_group_cells);
    }

    private makeMainFunnelLine(): MainFunnelLine {
        const main_funnel_cells = this.FunnelHeaders.mapWithAround(
            (funnel, prev, next) => {
                return new MainFunnelCell(funnel, {
                    IsSelected: this.selection.selectedMainFunnel(funnel.Key),
                    BackgroundColor: this.ColorScheme.FunnelColor,
                    PrevFunnelKeyOrNull: prev ? prev.Key : null,
                    NextFunnelKeyOrNull: next ? next.Key : null,
                    Collaborators: this.collaborator_focus.getFocusUserLabelsInMainFunnelCell(funnel.Key),
                })
            });
        return new MainFunnelLine(main_funnel_cells);
    }

    private makeContentCells(): ContentCells {
        const content_cells = this.cells.ContentHeaders.mapWithAround((content_header, prev_content_header, next_content_header) =>
            this.cells.FunnelHeaders.mapWithAround((funnel, prev_funnel, next_funnel) => {
                const cell_content = this.cells.ContentCellMatrix.get(funnel.Key, content_header.Key);
                const content_key_pair = new ContentKeyPair(content_header.Key, funnel.Key);

                return new ContentCell(
                    content_key_pair, cell_content,
                    {
                        IsSelected: this.selection.selectedContentCell(content_key_pair),
                        IsEdit: this.selection.selectedAndEditContentCell(content_key_pair),
                        HiddenBy: makeHiddenBy(funnel.IsHidden, content_header.IsHidden),
                        Width: funnel.Size,
                        Height: content_header.Size,
                        EditorType: content_header.Type,
                        ActionLineColor: this.ColorScheme.GroupColor,

                        LeftCellKeyPairOrNull: prev_funnel ? new ContentKeyPair(content_header.Key, prev_funnel.Key) : null,
                        RightCellKeyPairOrNull: next_funnel ? new ContentKeyPair(content_header.Key, next_funnel.Key) : null,
                        UpCellKeyPairOrNull: prev_content_header ? new ContentKeyPair(prev_content_header.Key, funnel.Key) : null,
                        DownCellKeyPairOrNull: next_content_header ? new ContentKeyPair(next_content_header.Key, funnel.Key) : null,

                        Collaborators: this.collaborator_focus.getFocusUserLabelsInContentCell(content_key_pair),
                    }
                );
            })
        ).flat().flat();
        return new ContentCells(content_cells);
    }

    private makeRoleLines(): RoleLines {

        const role_lines = this.cells.RoleHeaders.mapWithAround((role_header, above_role, below_role) => {

            const role_cells = RoleLine.makeVisibleCells(this.cells.FunnelHeaders.map(f => {
                    return new RoleCell(
                        new RoleKeyPair(role_header.Key, f.Key),
                        this.cells.RoleCellMatrix.get(f.Key, role_header.Key)
                    )
                }), this.merge,
                v => {
                    const column_start = v.offset + 1;
                    const span_num = v.keys.length;
                    const grid_column = `${column_start}` + (1 < span_num ? `/span ${span_num}` : '');

                    const has_next_cell = v.next !== null;
                    const is_joined = 1 < v.keys.length;

                    return new VisibleRoleCell(v.cell, {
                        IsSelected: this.selection.selectedRoleCell(v.cell.KeyPair),
                        BackgroundColor: this.ColorScheme.RoleColor,
                        GridColumn: this.merge ? grid_column : 'auto',
                        PrevRoleKeyPairOrNull: v.prev ? new RoleKeyPair(role_header.Key, v.prev) : null,
                        NextRoleKeyPairOrNull: v.next ? new RoleKeyPair(role_header.Key, v.next) : null,
                        AboveRoleKeyPairOrNull: above_role ? new RoleKeyPair(above_role.Key, v.cell.Key) : null,
                        BelowRoleKeyPairOrNull: below_role ? new RoleKeyPair(below_role.Key, v.cell.Key) : null,
                        IsJoinable: has_next_cell,
                        IsSeparatable: is_joined,
                        JoinedKeys: v.keys,
                        Collaborators: this.collaborator_focus.getFocusUserLabelsInRoleCell(v.cell.KeyPair),
                    });
                }
            );

            return new RoleLine(role_header, role_cells);
        });

        return new RoleLines(role_lines);
    }

    get BuiltFunnelGroupLine() {
        return this._funnel_group_line;
    }

    get BuiltMainFunnelLine() {
        return this._main_funnel_line;
    }

    get BuiltContentCells() {
        return this._content_cells;
    }

    get BuiltRoleLines() {
        return this._role_lines;
    }


    buildFunnelGridTemplateRows() {
        return this.funnel_type === 'single' ? '32px' : '32px 32px';
    }

    buildFunnelHeaderCellGridRow() {
        return this.funnel_type === 'single' ? 'auto' : 'auto  / span 2';
    }

    get Title(): string {
        return this.title;
    }

    setTitle(title: string) {
        return this.newMgr(() => {
            return {title};
        });
    }

    get Background(): string {
        return this.background_key;
    }

    get BackgroundImageStyle(): string {
        return this.background_key ? 'url(' + BackgroundKey2Src(this.background_key) + ')' : 'none';
    }

    setBackgroundKey(background_key: string) {
        return this.newMgr(() => {
            return {background_key};
        });
    }

    get ColorScheme(): ColorScheme {
        return findColorSchemeOrDefault(this.color_scheme_key);
    }

    setColorSchemeKey(color_scheme_key: string) {
        return this.newMgr(() => {
            return {color_scheme_key};
        });
    }


    setFunnelType(funnel_type: FunnelType) {
        return this.newMgr(() => {
            return {funnel_type};
        });
    }

    get IsVisibleFunnelGroupLine() {
        return this.funnel_type === 'double';
    }

    get LineHeaderWidth() {
        return 150;
    }

    get MapWidth() {
        return this.LineHeaderWidth /* content header */
            + this.FunnelHeaders.Width
    }

    get FunnelHeaders() {
        return this.cells.FunnelHeaders;
    }

    get ContentHeaders() {
        return this.cells.ContentHeaders;
    }

    get RoleHeaders() {
        return this.cells.RoleHeaders;
    }

    getRoleCellData(funnel_key: FunnelKey, role_key: RoleKey) {
        return this.cells.RoleCellMatrix.get(funnel_key, role_key);
    }

    getContentCellData(funnel_key: FunnelKey, content_key: ContentKey) {
        return this.cells.ContentCellMatrix.get(funnel_key, content_key);
    }

    get HasHiddenCell() {
        return this.FunnelHeaders.HasHidden || this.ContentHeaders.HasHidden;
    }

    get Selection(): CjmapSelection {
        return this.selection;
    }

    get IsJoinable(): boolean {
        return (this.Selection.FunnelGroupCellIsSelected && this.BuiltFunnelGroupLine.getSelectedCellOrNull()?.State.IsJoinable === true)
            || (this.Selection.RoleCellIsSelected && this.BuiltRoleLines.getSelectedCellOrNull()?.State.IsJoinable === true);
    }

    get IsSeparatable(): boolean {
        return (this.Selection.FunnelGroupCellIsSelected && this.BuiltFunnelGroupLine.getSelectedCellOrNull()?.State.IsSeparatable === true)
            || (this.Selection.RoleCellIsSelected && this.BuiltRoleLines.getSelectedCellOrNull()?.State.IsSeparatable === true)
    }

    changeRoleCellName(role_key: RoleKey, funnel_keys: FunnelKey[], name: string) {
        if (funnel_keys.length === 0) {
            throw new Error('empty funnel key');
        }

        const cells = this.cells.changeRoleCellName(role_key, funnel_keys, name);
        return this.newMgr(() => {
            return {cells};
        });
    }

    selectFunnelGroup(funnel_key: FunnelKey) {
        const selection = this.selection.selectFunnelGroupCell(funnel_key);
        return this.newMgr(() => {
            return {selection};
        });
    }

    selectMainFunnel(funnel_key: FunnelKey) {
        const selection = this.selection.selectMainFunnelCell(funnel_key);
        return this.newMgr(() => {
            return {selection};
        });
    }

    selectContentCell(key_pair: ContentKeyPair) {
        const selection = this.selection.selectContentCell(key_pair);
        return this.newMgr(() => {
            return {selection};
        });
    }

    selectRoleCell(key_pair: RoleKeyPair) {
        const selection = this.selection.selectRoleCell(key_pair);
        return this.newMgr(() => {
            return {selection};
        });
    }

    editCell() {
        const selection = this.selection.editContentCell();
        return this.newMgr(() => {
            return {selection};
        });
    }

    endEditCell() {
        const selection = this.selection.endEditContentCell();
        return this.newMgr(() => {
            return {selection};
        });
    }

    clearSelect() {
        const selection = this.selection.clearSelect();
        return this.newMgr(() => {
            return {selection};
        });
    }

    resizeCell(cell_key: ContentKeyPair, width: number, height: number) {
        if (width <= 0 || height <= 0) {
            return null;
        }

        return this.newMgr(() => {
            return {
                cells: this.cells
                    .resizeFunnel(cell_key.FunnelKey, width)
                    .resizeContentLine(cell_key.LineKey, height)
            };
        });
    }

    growCellHeight(cell_key: ContentKeyPair, height: number) {
        const line_key = cell_key.LineKey;
        if (height === 0 || height <= this.cells.ContentHeaders.findByKey(line_key).Size) {
            return null;
        }

        return this.newMgr(() => {
            return {cells: this.cells.resizeContentLine(line_key, height)};
        });
    }

    moveFunnel(funnel_key: FunnelKey, position: number) {
        return this.newMgr(() => {
            return {
                cells: this.cells.moveFunnel(funnel_key, position),
                merge: true,
                selection: this.selection.clearSelect()
            }
        });
    }

    moveContentLine(content_key: ContentKey, position: number) {
        const new_cells = this.cells.moveContentLine(content_key, position);
        return this.newMgr(() => {
            return {
                cells: new_cells,
                selection: this.selection.clearSelect(),
            }
        });
    }

    moveRoleLine(role_key: RoleKey, position: number) {
        const cells = this.cells.moveRoleLine(role_key, position);
        return this.newMgr(() => {
            return {cells};
        });
    }

    createFunnelKey(): FunnelKey {
        return this.cells.createFunnelKey();
    }

    createContentKey(): ContentKey {
        return this.cells.createContentKey();
    }

    createRoleKey(): RoleKey {
        return this.cells.createRoleKey();
    }


    createFunnel() {
        return Funnel.createDefault(this.createFunnelKey());
    }

    appendFunnel(funnel: Funnel, position: number) {
        const cells = this.cells.appendFunnel(funnel, position);
        return this.newMgr(() => {
            return {cells};
        });
    }

    removeFunnel(key: FunnelKey) {
        const cells = this.cells.deleteFunnel(key);
        return this.newMgr(() => {
            return {cells};
        });
    }

    hideFunnels(funnel_keys: FunnelKey[]) {
        if (funnel_keys.length === 0) {
            throw new Error('empty funnel key');
        }

        return this.newMgr(() => {
            return {cells: this.cells.hideFunnels(funnel_keys)};
        });
    }

    showFunnels(funnel_keys: FunnelKey[]) {
        if (funnel_keys.length === 0) {
            throw new Error('empty funnel key');
        }

        return this.newMgr(() => {
            return {cells: this.cells.showFunnels(funnel_keys)};
        });
    }

    hideLine(content_keys: ContentKey[]) {
        if (content_keys.length === 0) {
            throw new Error('empty content key');
        }

        return this.newMgr(() => {
            return {cells: this.cells.hideContentLines(content_keys)};
        });
    }

    showLine(content_keys: ContentKey[]) {
        if (content_keys.length === 0) {
            throw new Error('empty content key');
        }

        return this.newMgr(() => {
            return {cells: this.cells.showContentLines(content_keys)};
        });
    }

    showAllCell() {
        return this.newMgr(() => {
            return {cells: this.cells.showAllFunnelAndContentLine()};
        });
    }


    createContentHeader(editor_type: EditorType) {
        const content_key = this.createContentKey();
        return ContentHeader.createDefault(content_key, editor_type);
    }

    appendContent(content_header: ContentHeader, position: number) {
        const new_cells = this.cells.appendContentHeader(content_header, position);
        return this.newMgr(() => {
            return {cells: new_cells};
        });
    }

    removeContent(key: ContentKey) {
        const new_cells = this.cells.deleteContentLine(key);
        return this.newMgr(() => {
            return {cells: new_cells};
        });
    }

    createRoleHeader() {
        return RoleHeader.createDefault(this.createRoleKey());
    }

    appendRole(role: RoleHeader, position: number) {
        const new_cells = this.cells.appendRoleHeader(role, position);
        return this.newMgr(() => {
            return {cells: new_cells};
        });
    }

    removeRole(role_key: RoleKey) {
        const new_cells = this.cells.deleteRoleLine(role_key);
        return this.newMgr(() => {
            return {cells: new_cells};
        });
    }

    changeFunnelName(funnel_key: FunnelKey, new_name: string) {
        return this.newMgr(() => {
            return {cells: this.cells.renameFunnel(funnel_key, new_name)};
        });
    }

    changeFunnelGroup(funnel_keys: FunnelKey[], new_group: string) {
        if (funnel_keys.length === 0) {
            throw new Error('empty funnel key');
        }

        return this.newMgr(() => {
            return {cells: this.cells.changeFunnelGroup(funnel_keys, new_group)};
        });
    }

    changeContentHeaderName(content_key: ContentKey, new_name: string) {
        return this.newMgr(() => {
            return {cells: this.cells.renameContentHeader(content_key, new_name)};
        });
    }

    changeRoleHeaderName(role_key: RoleKey, new_name: string) {
        return this.newMgr(() => {
            return {cells: this.cells.renameRoleHeader(role_key, new_name)};
        });
    }

    setData(cell_key: ContentKeyPair, value: CellContent, width: number | null, height: number | null) {

        if ((width !== null && width <= 0) || (height !== null && height <= 0)) {
            return this;
        }

        return this.newMgr(() => {
            let new_cells = this.cells.updateCellContent(cell_key, value);
            if (width !== null) {
                new_cells = new_cells.resizeFunnel(cell_key.FunnelKey, width);
            }
            if (height !== null) {
                new_cells = new_cells.resizeContentLine(cell_key.LineKey, height);
            }

            return {cells: new_cells};
        });
    }


    moveToCell(direction: MoveDirection) {

        if (this.Selection.NotSelected) {
            return this;
        }

        if (this.Selection.RoleCellIsSelected) {
            const cell = this.BuiltRoleLines.getSelectedCellOrNull();
            if (cell === null) {
                return this;
            }

            if (direction === 'left') {
                if (cell.State.PrevRoleKeyPairOrNull) {
                    return this.selectRoleCell(cell.State.PrevRoleKeyPairOrNull);
                }
            } else if (direction === 'right') {
                if (cell.State.NextRoleKeyPairOrNull) {
                    return this.selectRoleCell(cell.State.NextRoleKeyPairOrNull);
                }
            } else if (direction === 'up') {
                if (cell.State.AboveRoleKeyPairOrNull) {
                    return this.selectRoleCell(cell.State.AboveRoleKeyPairOrNull);
                }
            } else if (direction === 'down') {
                if (cell.State.BelowRoleKeyPairOrNull) {
                    return this.selectRoleCell(cell.State.BelowRoleKeyPairOrNull);
                } else {
                    if (this.IsVisibleFunnelGroupLine) {
                        return this.selectFunnelGroup(cell.Key);
                    } else {
                        return this.selectMainFunnel(cell.Key);
                    }
                }
            }

        } else if (this.Selection.FunnelGroupCellIsSelected) {
            const cell = this.BuiltFunnelGroupLine.getSelectedCellOrNull();
            if (cell === null) {
                return this;
            }
            if (direction === 'left') {
                if (cell.State.PrevFunnelKeyOrNull) {
                    return this.selectFunnelGroup(cell.State.PrevFunnelKeyOrNull);
                }
            } else if (direction === 'right') {
                if (cell.State.NextFunnelKeyOrNull) {
                    return this.selectFunnelGroup(cell.State.NextFunnelKeyOrNull);
                }
            } else if (direction === 'up') {
                if (this.RoleHeaders.Exists) {
                    return this.selectRoleCell(new RoleKeyPair(this.RoleHeaders.LastHeader.Key, cell.Key))
                }
            } else if (direction === 'down') {
                return this.selectMainFunnel(cell.Key);
            }
        } else if (this.Selection.MainFunnelCellIsSelected) {
            const cell = this.BuiltMainFunnelLine.getSelectedCellOrNull();
            if (cell === null) {
                return this;
            }

            if (direction === 'left') {
                if (cell.State.PrevFunnelKeyOrNull) {
                    return this.selectMainFunnel(cell.State.PrevFunnelKeyOrNull);
                }
            } else if (direction === 'right') {
                if (cell.State.NextFunnelKeyOrNull) {
                    return this.selectMainFunnel(cell.State.NextFunnelKeyOrNull);
                }
            } else if (direction === 'up') {
                if (this.IsVisibleFunnelGroupLine) {
                    return this.selectFunnelGroup(cell.Funnel.Key);
                } else if (this.RoleHeaders.Exists) {
                    return this.selectRoleCell(new RoleKeyPair(this.RoleHeaders.LastHeader.Key, cell.Funnel.Key));
                }

            } else if (direction === 'down') {
                return this.selectContentCell(new ContentKeyPair(this.ContentHeaders.FirstHeader.Key, cell.Funnel.Key));
            }

        } else if (this.Selection.ContentCellIsSelected) {
            const cell = this.BuiltContentCells.getSelectedCellOrNull();
            if (cell === null) {
                return this;
            }

            if (direction === 'left') {
                if (cell.State.LeftCellKeyPairOrNull) {
                    return this.selectContentCell(cell.State.LeftCellKeyPairOrNull);
                }
            } else if (direction === 'right') {
                if (cell.State.RightCellKeyPairOrNull) {
                    return this.selectContentCell(cell.State.RightCellKeyPairOrNull);
                }

            } else if (direction === 'up') {
                if (cell.State.UpCellKeyPairOrNull) {
                    return this.selectContentCell(cell.State.UpCellKeyPairOrNull);
                } else {
                    return this.selectMainFunnel(cell.FunnelKey);
                }

            } else if (direction === 'down') {
                if (cell.State.DownCellKeyPairOrNull) {
                    return this.selectContentCell(cell.State.DownCellKeyPairOrNull);
                }
            }
        }

        return this;
    }

    get Persona(): CjmapPersona {
        return this.persona;
    }

    updatePersona(persona: CjmapPersona) {
        return this.newMgr(() => {
            return {persona: persona};
        });
    }

    countOnlineUser(): number {
        return this.collaborator_focus.userCount + 1 /* 自分自身を加えている */;
    }
    listCollaboratorFocusUserEmails(): string[] {
        return this.collaborator_focus.listAllUserLabel();
    }
    hasCollaboratorFocusUserEmail(user_id: string) {
        return this.collaborator_focus.hasUser(user_id);
    }

    updateCollaboratorFocusUser(user_id: string, user_email: string) {
        return this.newMgr(() => {
            return {collaborator_focus: this.collaborator_focus.updateUser(user_id, user_email)};
        });
    }
    removeCollaboratorFocusUser(user_id: string) {
        return this.newMgr(() => {
            return {collaborator_focus: this.collaborator_focus.removeUser(user_id)};
        });
    }

    updateCollaboratorFocus(user_id: string, selection: CjmapSelection) {
        return this.newMgr(() => {
            return {collaborator_focus: this.collaborator_focus.updateFocus(user_id, selection)};
        });
    }

    removeCollaboratorFocus(user_id: string) {
        return this.newMgr(() => {
            return {collaborator_focus: this.collaborator_focus.removeFocus(user_id)};
        });
    }

    restoreSelection(selection: CjmapSelection) {
        return this.newMgr(() => {
            return {selection: selection};
        });
    }


    private refresher?: (mgr: CJMapManager) => void = undefined;
    private changed: CJMapManager[] = [];

    setRefresher(refresher: (mgr: CJMapManager) => void) {
        this.refresher = refresher;
    }

    apply() {
        if (this.changed.length !== 0) {
            is_dev() && debug_log("apply", ...(this.changed.map(m => m.toJourneyMapJSONWithInternalVersion())) /*, new Error().stack */);
            this.changed.splice(0);
        }

        this.refresher?.(this);
    }

    private newMgr(updater: () => CJmapManagerConstruction) {

        if (this.refresher && this.changed.length === 0) {
            this.changed.push(this);
        }

        const mgr = this.clone(this._version + 1, updater());

        if (this.refresher) {
            this.changed.push(mgr);
        }

        mgr.refresher = this.refresher;
        mgr.changed = this.changed;
        return mgr;
    }

    UnmergeTemporary() {
        return this.newMgr(() => {
            return {merge: false};
        });
    }

    remerge() {
        return this.newMgr(() => {
            return {merge: true};
        });
    }


    toJourneyMapJSONString() {
        return JSON.stringify(this.toJourneyMapJSON());
    }


    /**
     * @see SerializableJourneyMap.fromJSON
     */
    toJourneyMapJSON() {
        return {
            title: this.title,
            background_key: this.background_key,
            color_scheme_key: this.color_scheme_key,
            funnel_type: this.funnel_type,
            cells: this.cells.toJSON(),
            persona: this.persona.toJSON(),
        }
    }

    toJourneyMapJSONWithInternalVersion() {
        return {
            _version: this._version,
            ...this.toJourneyMapJSON()
        }
    }

    setNewJourneyMap(journey_map: SerializableJourneyMap) {
        debug_log("setNewJourneyMap", journey_map);

        return new CJMapManager(0, {
            title: journey_map.title,
            background_key: journey_map.background_key,
            color_scheme_key: journey_map.color_scheme_key,
            funnel_type: journey_map.funnel_type,
            cells: journey_map.cells,
            persona: journey_map.persona,
        });
    }

    //
}
